import { useEffect } from 'react'

import { isStandaloneDisplay } from '../lib/pwaInstall'

const MANAGED_ATTRIBUTE = 'data-lupa-seo'
const DEFAULT_IMAGE = '/og-image.png?v=bff970247ed8'
/** Browser chrome over an ordinary tab, where the pages underneath are light. */
export const BROWSER_THEME_COLOR = '#6b7280'
/**
 * The installed app paints Android's status bar with theme-color; its shell is dark (manifest
 * background_color), so the browser gray turned the bar into a pale band above it.
 */
export const STANDALONE_THEME_COLOR = '#0d0f11'

type JsonLd = Record<string, unknown>

export interface SEOHeadProps {
  title: string
  description: string
  canonical?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogType?: string
  ogUrl?: string
  twitterCard?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  robots?: string
  author?: string
  jsonLd?: JsonLd
}

type MetaTag =
  | { name: string; content: string }
  | { property: string; content: string }

function absoluteUrl(value: string) {
  return new URL(value, window.location.origin).href
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  ogUrl,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots = 'index, follow',
  author = 'Лупа',
  jsonLd,
}: SEOHeadProps) {
  const serializedJsonLd = jsonLd ? JSON.stringify(jsonLd) : undefined

  useEffect(() => {
    const managedSelector = `[${MANAGED_ATTRIBUTE}]`
    const previousTitle = document.title
    const previousManagedNodes = Array.from(
      document.head.querySelectorAll<HTMLElement>(managedSelector),
      (node) => node.cloneNode(true),
    )

    // Only tags carrying our marker are replaced. Viewport, referrer, PWA links,
    // verification tags and any metadata owned by integrations stay untouched.
    document.head.querySelectorAll(managedSelector).forEach((node) => node.remove())
    document.title = title

    const resolvedCanonical = canonical ? absoluteUrl(canonical) : undefined
    // Public tracking links carry a secret in the query string. The pathname-only
    // fallback keeps that token (and all other query parameters) out of og:url.
    const resolvedOgUrl = absoluteUrl(ogUrl || resolvedCanonical || window.location.pathname)
    const resolvedOgImage = absoluteUrl(ogImage)
    const resolvedTwitterImage = absoluteUrl(twitterImage || ogImage)

    const metaTags: MetaTag[] = [
      { name: 'description', content: description },
      { name: 'author', content: author },
      { name: 'robots', content: robots },
      { name: 'theme-color', content: isStandaloneDisplay() ? STANDALONE_THEME_COLOR : BROWSER_THEME_COLOR },
      { property: 'og:title', content: ogTitle || title },
      { property: 'og:description', content: ogDescription || description },
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: resolvedOgUrl },
      { property: 'og:site_name', content: 'Лупа' },
      { property: 'og:locale', content: 'ru_RU' },
      { property: 'og:image', content: resolvedOgImage },
      { property: 'og:image:width', content: ogImage === DEFAULT_IMAGE ? '1731' : '1200' },
      { property: 'og:image:height', content: ogImage === DEFAULT_IMAGE ? '909' : '630' },
      { name: 'twitter:card', content: twitterCard },
      { name: 'twitter:title', content: twitterTitle || ogTitle || title },
      { name: 'twitter:description', content: twitterDescription || ogDescription || description },
      { name: 'twitter:image', content: resolvedTwitterImage },
    ]

    metaTags.forEach((tag) => {
      const meta = document.createElement('meta')
      meta.setAttribute(MANAGED_ATTRIBUTE, '')
      if ('name' in tag) meta.name = tag.name
      else meta.setAttribute('property', tag.property)
      meta.content = tag.content
      document.head.appendChild(meta)
    })

    if (resolvedCanonical) {
      const canonicalLink = document.createElement('link')
      canonicalLink.setAttribute(MANAGED_ATTRIBUTE, '')
      canonicalLink.rel = 'canonical'
      canonicalLink.href = resolvedCanonical
      document.head.appendChild(canonicalLink)
    }

    if (serializedJsonLd) {
      const script = document.createElement('script')
      script.setAttribute(MANAGED_ATTRIBUTE, '')
      script.type = 'application/ld+json'
      script.textContent = serializedJsonLd
      document.head.appendChild(script)
    }

    return () => {
      document.head.querySelectorAll(managedSelector).forEach((node) => node.remove())
      previousManagedNodes.forEach((node) => document.head.appendChild(node))
      document.title = previousTitle
    }
  }, [
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    ogUrl,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    robots,
    author,
    serializedJsonLd,
  ])

  return null
}
